import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Sparkles, CheckCircle2, Briefcase, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const BrandWaitlist = () => {
  const [formData, setFormData] = useState({
    brand_name: '',
    website: '',
    industry: '',
    country: '',
    marketing_email: '',
    creator_niches: []
  });
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const availableNiches = [
    'AI Tools & Productivity',
    'Fitness & Wellness',
    'Beauty & Cosmetics',
    'Technology',
    'Finance',
    'Education',
    'Gaming',
    'Travel',
    'Food',
    'Fashion',
    'Lifestyle'
  ];

  const toggleNiche = (niche) => {
    if (selectedNiches.includes(niche)) {
      setSelectedNiches(selectedNiches.filter(n => n !== niche));
    } else {
      setSelectedNiches([...selectedNiches, niche]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.brand_name || !formData.website || !formData.industry || !formData.marketing_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedNiches.length === 0) {
      toast.error('Please select at least one creator niche');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/brands/waitlist`, {
        ...formData,
        creator_niches: selectedNiches
      });
      setSubmitted(true);
      toast.success('Successfully joined the waitlist!');
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>You're on the Waitlist!</h1>
          <p className="text-lg text-slate-600 mb-8">
            Thank you for your interest. We'll notify you when the brand portal is ready and share insights about creators interested in your brand.
          </p>
          <Link to="/">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" style={{fontFamily: 'Manrope'}} data-testid="brands-heading">
              Join Sponsiq for Brands
            </h1>
            <p className="text-lg text-slate-600">
              See which creators want to collaborate with your brand and launch campaigns when ready.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Creator Demand</h3>
              <p className="text-sm text-slate-600">See how many creators want to work with your brand</p>
            </Card>

            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Audience Reach</h3>
              <p className="text-sm text-slate-600">Track total potential audience reach</p>
            </Card>

            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Launch Campaigns</h3>
              <p className="text-sm text-slate-600">Create sponsorship campaigns when you're ready</p>
            </Card>
          </div>

          {/* Form */}
          <Card className="p-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>Join the Waitlist</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="brand_name">Brand Name *</Label>
                <Input
                  id="brand_name"
                  type="text"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                  placeholder="Your brand name"
                  required
                  data-testid="brand-name-input"
                />
              </div>

              <div>
                <Label htmlFor="website">Website *</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://yourbrand.com"
                  required
                  data-testid="website-input"
                />
              </div>

              <div>
                <Label htmlFor="industry">Industry *</Label>
                <Input
                  id="industry"
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  placeholder="e.g., SaaS, E-commerce, Health & Wellness"
                  required
                  data-testid="industry-input"
                />
              </div>

              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  placeholder="e.g., United States"
                  required
                  data-testid="country-input"
                />
              </div>

              <div>
                <Label htmlFor="marketing_email">Marketing Email *</Label>
                <Input
                  id="marketing_email"
                  type="email"
                  value={formData.marketing_email}
                  onChange={(e) => setFormData({...formData, marketing_email: e.target.value})}
                  placeholder="marketing@yourbrand.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label>Creator Niches You're Interested In *</Label>
                <p className="text-sm text-slate-600 mb-3">Select all that apply</p>
                <div className="flex flex-wrap gap-2">
                  {availableNiches.map((niche) => (
                    <button
                      key={niche}
                      type="button"
                      onClick={() => toggleNiche(niche)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        selectedNiches.includes(niche)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`niche-btn-${niche.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {niche}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-lg font-semibold"
                data-testid="submit-waitlist-btn"
              >
                {submitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BrandWaitlist;
